import { datadogRum } from '@datadog/browser-rum';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { ReactNode, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  RouteObject,
  Routes as RouterRoutes,
  createRoutesFromElements,
  matchRoutes,
  useLocation,
} from 'react-router-dom';

import { ClearState } from '../ClearState';
import { AppointmentLinkChangedPage } from '../appointment-link-changed/AppointmentLinkChangedPage';
import { LoginSuccessPage } from '../auth/LoginSuccessPage';
import { LoginErrorPageWrapper } from '../auth/error/LoginErrorPage';
import { LoginRedirectCallbackPageWrapper } from '../auth/redirect/LoginRedirectCallbackPage';
import { BrandingDevTools } from '../components/branding/dev/BrandingDevTools';
import { PROVIDER_PATH, isDevMode } from '../config';
import { Error404Page } from '../error/404Page';
import { Page as ProviderPage, ProviderProtectedWrapper as ProviderProtected } from '../provider';
import { ToastRegions } from '../toasts';
import { UserType } from '../types';
import { Routes as VisitorRoutes } from '../visitor';
import { VisitorRouteRedirect } from '../visitor/nav/VisitorRouteRedirect';
import { WithVisitorRoutePreRender } from '../visitor/nav/WithVisitorRoutePreRender';
import {
  LOGIN_CALLBACK_PATH,
  LOGIN_ERROR_PAGE_PATH,
  PROVIDER_PATH_ID,
  RELOGIN_SUCCESS_PATH,
  VISITOR_PATH_ID,
} from './config';

const DevtoolsAtom = atom<ReactNode | null>(null);

function DevToolsWrapper() {
  const devtools = useAtomValue(DevtoolsAtom);
  return devtools;
}

function WithDevTools({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <DevToolsWrapper />
      <BrandingDevTools />
    </>
  );
}

/* eslint-disable react-refresh/only-export-components */
/**
 * Returns a tuple of equivalent representations of the application's routes.
 */
export function getRoutes(devPage?: ReactNode): [ReactNode, RouteObject[]] {
  const routesNode = (
    <>
      <Route
        id={VISITOR_PATH_ID}
        path={'/:providerShortId/:patientShortId/*'}
        element={
          <WithDevTools>
            <VisitorRouteRedirect>
              <WithVisitorRoutePreRender>
                <ToastRegions userType={UserType.CLIENT} />
                <VisitorRoutes />
              </WithVisitorRoutePreRender>
            </VisitorRouteRedirect>
          </WithDevTools>
        }
      />
      <Route
        id={PROVIDER_PATH_ID}
        path={PROVIDER_PATH}
        element={
          <WithDevTools>
            <ProviderProtected>
              <ToastRegions userType={UserType.PROVIDER} />
              <ProviderPage />
            </ProviderProtected>
          </WithDevTools>
        }
      />
      {devPage && <Route id='dev' path='/dev' element={devPage} />}
      <Route id='clear-state' path='/clear-state' element={<ClearState />} />
      <Route id='404' path='/404' element={<Error404Page />} />
      <Route id='appointment-link-changed' path='/appointment-link-changed' element={<AppointmentLinkChangedPage />} />
      <Route id='relogin-success' path={RELOGIN_SUCCESS_PATH} element={<LoginSuccessPage />} />
      <Route id='home' path='/' element={<Navigate to={PROVIDER_PATH} />} />
      {/**
       * This is where users should be redirected to after logging in. The
       * Telehealth SPA's Auth0 provider is configured to then immediately use the
       * provided `appState.returnTo` value to reroute users to the correct
       * page.
       */}
      <Route id='login-callback' path={LOGIN_CALLBACK_PATH} element={<LoginRedirectCallbackPageWrapper />} />
      <Route id='login-error' path={LOGIN_ERROR_PAGE_PATH} element={<LoginErrorPageWrapper />} />
      <Route id='default' path='*' element={<Error404Page />} />
    </>
  );

  const routeObjects = createRoutesFromElements(routesNode);

  return [routesNode, routeObjects];
}
/* eslint-enable react-refresh/only-export-components */

export function Routes() {
  const [DevPage, setDevPage] = useState<ReactNode>();
  const setDevTools = useSetAtom(DevtoolsAtom);

  const [routes] = getRoutes(DevPage);

  // Datadog clobbers our routes because they have alphanumeric ids in them
  // https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#react-router-instrumentation
  const location = useLocation();
  useEffect(
    function startDatadogRumViewForRoute() {
      const matches = matchRoutes(createRoutesFromElements(routes), location.pathname);
      if (matches) {
        const {
          pathname,
          route: { id },
        } = matches[0];
        const viewName = id ? `${id} (${pathname})` : pathname;
        datadogRum.startView(viewName);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname],
  );

  useEffect(
    function handleDevMode() {
      // Only load the dev page if Vite is running in dev mode
      if (isDevMode) {
        import('../dev/Page').then((module) => {
          const DevPageComponent = module.Page;
          setDevPage(<DevPageComponent />);
        });
        import('../dev/DevTools').then((module) => {
          const DevToolsComponent = module.DevTools;
          setDevTools(<DevToolsComponent />);
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return <RouterRoutes>{routes}</RouterRoutes>;
}
